import { useCallback, useEffect, useMemo, useState } from "react";
import { CommonActions, useNavigation } from "@react-navigation/native";
import {
  View,
  Text,
  Pressable,
  Image,
  Linking,
  NativeScrollEvent,
  NativeSyntheticEvent,
  type ImageSourcePropType,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
  X,
  Heart,
  Wrench,
  Clock,
  Timer,
  DollarSign,
  CalendarDays,
  Info,
  CheckCircle,
  Lightbulb,
  ExternalLink,
  Beaker,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  FlaskConical,
  TrendingUp,
  TrendingDown,
  Minus,
  XCircle,
  AlertCircle,
  Target,
  BookOpen,
  Venus,
  Mars,
  Activity,
  ClipboardCheck,
  FileText,
  Moon,
  ChevronLeft,
  Globe2,
  Tags,
} from "lucide-react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
  runOnJS,
} from "react-native-reanimated";
import type {
  ContentBlock,
  ContentBlockId,
  DataPointBody,
  TestBody,
  ProductBody,
} from "@src/packages/content";
import {
  getBlock,
  getDataPointsForProduct,
  getDataPointsForTest,
  getSampleTypeLabel,
} from "@src/packages/content";
import {
  IconButton,
  useScrollHaptics,
  springs,
  SectionTitle,
  Collapsible,
  BrownButton,
} from "@louise/moon-ui";
import {
  useTranslations,
  useLocale,
  type TranslationFunction,
} from "@src/packages/i18n";
import EvidenceLevelModal from "./EvidenceLevelModal";
import EvidenceSourceRows from "./EvidenceSourceRows";
import { getTestRouteTarget } from "../screens/access/testRouteIds";
import { EMBER } from "../../onboarding/theme";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import OrderKitButton from "./OrderKitButton";
import { getLocalProductImage } from "./productImages";
import FactSheetHeader, {
  FactSheetBadge,
  type FactSheetHeaderBadge,
} from "./FactSheetHeader";
import LinkedFactSheetText, {
  type FactSheetLinkTarget,
} from "./LinkedFactSheetText";
import { flagFromIso } from "./countryFlag";
import type { MainTabParamList } from "../types";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
// Maps the raw category value from CMS to a stable i18n key (learn.test_detail.category_*).
const CATEGORY_I18N_KEYS: Record<string, string> = {
  "blood-marker": "category_blood_marker",
  imaging: "category_imaging",
  genetic: "category_genetic",
  semen: "category_semen",
  monitoring: "category_monitoring",
  biopsy: "category_biopsy",
};

const TEST_ILLUSTRATIONS: Record<string, ImageSourcePropType> = {
  "test-chronic-endometritis-testing": require("../../assets/images/therapies/electron-microscope.png"),
};

const TEST_RESULT_TYPE_ILLUSTRATIONS: Record<
  string,
  Partial<Record<string, ImageSourcePropType>>
> = {
  "test-pgt-a": {
    Euploid: require("../../assets/images/testing/pgt-a-euploid.png"),
    Aneuploid: require("../../assets/images/testing/pgt-a-aneuploid.png"),
    Mosaic: require("../../assets/images/testing/pgt-a-mosaic.png"),
    "No-result": require("../../assets/images/testing/pgt-a-no-result.png"),
  },
};

type ResultGlyphKind =
  | "window-receptive"
  | "window-pre"
  | "window-post"
  | "microbiome-dominant"
  | "microbiome-dysbiotic"
  | "microbiome-pathogen"
  | "microbiome-low"
  | "genetic-clear"
  | "genetic-carrier"
  | "genetic-affected"
  | "genetic-pair-risk"
  | "chromosome-typical"
  | "chromosome-balanced"
  | "chromosome-unbalanced"
  | "chromosome-mosaic"
  | "unknown";

const TEST_RESULT_TYPE_GLYPHS: Record<
  string,
  Partial<Record<string, ResultGlyphKind>>
> = {
  "test-endometrial-receptivity-testing": {
    Receptive: "window-receptive",
    "Pre-receptive": "window-pre",
    "Post-receptive": "window-post",
    Inconclusive: "unknown",
  },
  "test-endometrial-receptivity-testing-products-era": {
    Receptive: "window-receptive",
    "Pre-receptive": "window-pre",
    "Post-receptive": "window-post",
    Inconclusive: "unknown",
  },
  "test-endometrial-receptivity-testing-products-erpeak": {
    Receptive: "window-receptive",
    "Pre-receptive": "window-pre",
    "Post-receptive": "window-post",
    Inconclusive: "unknown",
  },
  "test-endometrial-receptivity-testing-products-beready": {
    Receptive: "window-receptive",
    "Pre-receptive": "window-pre",
    "Post-receptive": "window-post",
    Inconclusive: "unknown",
  },
  "test-endometrial-microbiome-testing": {
    "Lactobacillus-dominant": "microbiome-dominant",
    Dysbiotic: "microbiome-dysbiotic",
    "Pathogen-associated": "microbiome-pathogen",
    "Low biomass or inconclusive": "microbiome-low",
  },
  "test-endometrial-microbiome-testing-products-emma": {
    "Lactobacillus-dominant": "microbiome-dominant",
    Dysbiotic: "microbiome-dysbiotic",
    "Pathogen-associated": "microbiome-pathogen",
    "Low biomass or inconclusive": "microbiome-low",
  },
  "test-endometrial-microbiome-testing-products-erbiome": {
    "Lactobacillus-dominant": "microbiome-dominant",
    Dysbiotic: "microbiome-dysbiotic",
    "Pathogen-associated": "microbiome-pathogen",
    "Low biomass or inconclusive": "microbiome-low",
  },
  "test-chronic-endometritis-testing": {
    "Not detected": "microbiome-dominant",
    Detected: "microbiome-pathogen",
    Inconclusive: "unknown",
  },
  "test-chronic-endometritis-testing-products-alice": {
    "Not detected": "microbiome-dominant",
    Detected: "microbiome-pathogen",
    Inconclusive: "unknown",
  },
  "test-endometrial-add-on-bundle": {
    "All components not flagged": "microbiome-dominant",
    "ERA non-receptive": "window-pre",
    "Microbiome or inflammation flagged": "microbiome-pathogen",
    "Mixed or inconclusive": "unknown",
  },
  "test-endometrial-add-on-bundle-products-endometrio": {
    "All components not flagged": "microbiome-dominant",
    "ERA non-receptive": "window-pre",
    "Microbiome or inflammation flagged": "microbiome-pathogen",
    "Mixed or inconclusive": "unknown",
  },
  "test-endometrial-add-on-bundle-products-er-complete": {
    "All components not flagged": "microbiome-dominant",
    "ERA non-receptive": "window-pre",
    "Microbiome or inflammation flagged": "microbiome-pathogen",
    "Mixed or inconclusive": "unknown",
  },
  "test-pgt-m": {
    Unaffected: "genetic-clear",
    Carrier: "genetic-carrier",
    "Affected or high-risk": "genetic-affected",
    "No-result": "unknown",
  },
  "test-pgt-sr": {
    "Normal or balanced": "chromosome-balanced",
    Unbalanced: "chromosome-unbalanced",
    "Mosaic or segmental": "chromosome-mosaic",
    "No-result": "unknown",
  },
  "test-pgt-p": {
    "Lower predicted risk": "genetic-clear",
    "Higher predicted risk": "genetic-affected",
    "Limited-use score": "unknown",
    "No-result": "unknown",
  },
  "test-carrier-screening": {
    "No carrier variant found": "genetic-clear",
    Carrier: "genetic-carrier",
    "Both partners carry the same condition": "genetic-pair-risk",
    "Uncertain or incomplete": "unknown",
  },
  "test-karyotype": {
    "Typical karyotype": "chromosome-typical",
    "Balanced rearrangement": "chromosome-balanced",
    "Unbalanced or extra/missing chromosome": "chromosome-unbalanced",
    "Mosaic or inconclusive": "chromosome-mosaic",
  },
};

const EVIDENCE_STRENGTH_KEYS: Record<
  NonNullable<TestBody["evidenceStrength"]>,
  string
> = {
  strong: "evidence_strength_strong",
  moderate: "evidence_strength_moderate",
  emerging: "evidence_strength_emerging",
  mixed: "evidence_strength_mixed",
  debunked: "evidence_strength_debunked",
};

const EVIDENCE_COLORS: Record<
  NonNullable<TestBody["evidenceStrength"]>,
  string
> = {
  strong: EMBER.colors.green,
  moderate: EMBER.colors.gold,
  emerging: EMBER.colors.textLight,
  mixed: EMBER.colors.coral,
  debunked: "#C0392B",
};

const MINI_LABEL_TEXT_STYLE: TextStyle = {
  fontSize: 10,
  fontWeight: "700",
  textTransform: "uppercase",
  color: EMBER.colors.textFaint,
  letterSpacing: 0,
};

const COUNTRY_LABELS: Record<string, string> = {
  AE: "United Arab Emirates",
  AR: "Argentina",
  AT: "Austria",
  AU: "Australia",
  BE: "Belgium",
  BG: "Bulgaria",
  BR: "Brazil",
  CA: "Canada",
  CL: "Chile",
  CO: "Colombia",
  CY: "Cyprus",
  CZ: "Czechia",
  DE: "Germany",
  DK: "Denmark",
  EE: "Estonia",
  ES: "Spain",
  EU: "European Union",
  FI: "Finland",
  FR: "France",
  GB: "United Kingdom",
  GR: "Greece",
  HR: "Croatia",
  HU: "Hungary",
  IE: "Ireland",
  IN: "India",
  IT: "Italy",
  JP: "Japan",
  KR: "South Korea",
  LT: "Lithuania",
  LU: "Luxembourg",
  LV: "Latvia",
  MT: "Malta",
  MX: "Mexico",
  NL: "Netherlands",
  PE: "Peru",
  PL: "Poland",
  PT: "Portugal",
  RO: "Romania",
  SE: "Sweden",
  SI: "Slovenia",
  SK: "Slovakia",
  TR: "Turkey",
  TW: "Taiwan",
  US: "United States",
};

function getCountryLabel(iso: string, locale?: string): string {
  const code = iso.toUpperCase();
  if (typeof Intl !== "undefined" && Intl.DisplayNames) {
    try {
      const names = new Intl.DisplayNames([locale ?? "en"], { type: "region" });
      const localized = names.of(code);
      if (localized && localized !== code) return localized;
    } catch {
      /* fall through */
    }
  }
  return COUNTRY_LABELS[code] ?? code;
}

function getCommercialCategoryBadgeLabel(
  t: TranslationFunction,
  title?: string,
): string {
  if (!title) return t("learn.test_detail.view_all_related");
  const normalizedTitle = title.trim().toLowerCase();
  return t("learn.test_detail.view_all_prefix", {
    params: { category: normalizedTitle.replace(/\btesting$/, "tests") },
  });
}

/* ─── Detail Content ─── */

export function TestDetailContent({
  item,
  onClose,
  onBack,
  skipExitAnimation,
  onOpenMetric,
  onOpenFactSheet,
}: {
  item: ContentBlock<"test"> | ContentBlock<"product">;
  onClose: () => void;
  onBack?: () => void;
  skipExitAnimation?: boolean;
  onOpenMetric?: (metricKey: string) => void;
  onOpenFactSheet?: (target: FactSheetLinkTarget) => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("TestDetailContent");
  const insets = useSafeAreaInsets();
  const isProduct = item.kind === "product";
  const productBody = isProduct ? (item.body as ProductBody) : null;
  const localProductImage = isProduct
    ? getLocalProductImage(String(item.id))
    : undefined;
  const testBody = isProduct ? null : (item.body as unknown as TestBody);
  const testIllustration = !isProduct
    ? TEST_ILLUSTRATIONS[item.id as string]
    : undefined;
  const scale = useSharedValue(0.92);
  const opacity = useSharedValue(0);
  const onScrollHaptics = useScrollHaptics();
  const [evidenceModal, setEvidenceModal] = useState<string | null>(null);

  useState(() => {
    scale.value = withSpring(1, springs.sheet);
    opacity.value = withTiming(1, { duration: 250 });
  });

  const animatedStyle = useAnimatedStyle(() => ({
    flex: 1,
    overflow: "hidden" as const,
    transform: [{ scale: scale.value }],
    opacity: opacity.value,
  }));

  const handleClose = useCallback(() => {
    if (skipExitAnimation) {
      onClose();
      return;
    }
    scale.value = withSpring(0.92, springs.sheet);
    opacity.value = withTiming(0, { duration: 200 }, () => {
      runOnJS(onClose)();
    });
  }, [scale, opacity, onClose, skipExitAnimation]);

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      onScrollHaptics(e);
    },
    [onScrollHaptics],
  );

  // Build badges based on item kind
  const badges: FactSheetHeaderBadge[] = [];
  if (isProduct) {
    const productBody = item.body as ProductBody;
    for (const chip of productBody.chips ?? []) {
      badges.push({ label: chip });
    }
  } else if (testBody) {
    const parentTest = testBody.parentTest;
    if (parentTest) {
      const parentBlock = getBlock(parentTest.testId);
      badges.push({
        label:
          parentTest.badgeLabel ??
          getCommercialCategoryBadgeLabel(t, parentBlock?.title),
        icon: BookOpen,
        accent: true,
        onPress:
          onBack ??
          (onOpenFactSheet
            ? () =>
                onOpenFactSheet({
                  kind: "test",
                  id: String(parentTest.testId),
                })
            : undefined),
      });
    }
    const commercialProfile = testBody.commercialProductProfile;
    if (commercialProfile) {
      const categoryBlock = getBlock(commercialProfile.categoryTestId);
      badges.push({
        label: getCommercialCategoryBadgeLabel(t, categoryBlock?.title),
        icon: BookOpen,
        accent: true,
        onPress:
          onBack ??
          (onOpenFactSheet
            ? () =>
                onOpenFactSheet({
                  kind: "test",
                  id: String(commercialProfile.categoryTestId),
                })
            : undefined),
      });
    }
    if (item.tags.includes("her")) {
      badges.push({
        icon: Venus,
        accessibilityLabel: t("learn.test_detail.female_test_a11y"),
      });
    } else if (item.tags.includes("his")) {
      badges.push({
        icon: Mars,
        accessibilityLabel: t("learn.test_detail.male_test_a11y"),
      });
    }
    if (testBody.category) {
      const categoryKey = CATEGORY_I18N_KEYS[testBody.category];
      badges.push({
        label: categoryKey
          ? t(`learn.test_detail.${categoryKey}`)
          : testBody.category,
      });
    }
    if (testBody.atHome) {
      badges.push({ label: t("learn.test_detail.badge_at_home") });
    }
    if (testBody.clinicRequired) {
      badges.push({ label: t("learn.test_detail.badge_clinic_visit") });
    }
    if (commercialProfile) {
      badges.push({
        label: t("learn.test_detail.sold_by", {
          params: { company: commercialProfile.company },
        }),
      });
    }
    if (testBody.evidenceStrength) {
      badges.push({
        label: t(
          `learn.test_detail.${EVIDENCE_STRENGTH_KEYS[testBody.evidenceStrength]}`,
        ),
        icon: FlaskConical,
        color: EVIDENCE_COLORS[testBody.evidenceStrength],
      });
    }
  }

  return (
    <View style={{ flex: 1, overflow: "hidden" }}>
      <Image
        source={require("@louise/moon-ui/assets/images/onboarding-bg.jpg")}
        style={{
          position: "absolute",
          width: "115%",
          height: "115%",
          left: "-7.5%",
          top: "-7.5%",
        }}
        resizeMode="cover"
      />
      <Animated.View style={animatedStyle}>
        <View style={{ flex: 1, paddingTop: insets.top }}>
          <Animated.ScrollView
            contentContainerStyle={{
              paddingHorizontal: 16,
              paddingTop: SCREEN_PADDING_TOP,
              paddingBottom: 100,
              maxWidth: 520,
              width: "100%",
              alignSelf: "center",
            }}
            showsVerticalScrollIndicator={false}
            onScroll={handleScroll}
            scrollEventThrottle={16}
          >
            <View
              style={{
                ...GLASS_CARD_STYLE,
                paddingHorizontal: 24,
                paddingTop: 24,
                paddingBottom: 32,
              }}
            >
              {onBack ? (
                <View
                  style={{
                    position: "absolute",
                    top: 16,
                    left: 16,
                    zIndex: 2,
                  }}
                >
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel="Back"
                    onPress={() => {
                      // Chevron-only control; its accessibility label is the copy.
                      capturePress(pressLabel("Back"), "back");
                      onBack();
                    }}
                    hitSlop={8}
                    style={({ pressed }) => ({
                      width: 32,
                      height: 32,
                      borderRadius: 16,
                      backgroundColor: pressed
                        ? "rgba(0, 0, 0, 0.08)"
                        : "rgba(0, 0, 0, 0.05)",
                      alignItems: "center",
                      justifyContent: "center",
                    })}
                  >
                    <ChevronLeft size={17} color={EMBER.colors.textLight} />
                  </Pressable>
                </View>
              ) : null}

              {/* Close */}
              <View
                style={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  zIndex: 2,
                }}
              >
                <IconButton
                  onPress={handleClose}
                  hitSlop={8}
                  size={32}
                  surface="soft"
                  backgroundColor="rgba(0, 0, 0, 0.05)"
                  pressedBackgroundColor="rgba(0, 0, 0, 0.05)"
                  pressedOpacity={1}
                  accessibilityLabel="Close"
                >
                  <X size={16} color={EMBER.colors.textLight} />
                </IconButton>
              </View>

              <FactSheetHeader
                title={item.title}
                subtitle={
                  isProduct
                    ? item.subtitle
                    : item.subtitle ||
                      (testBody?.testName !== item.title
                        ? testBody?.testName
                        : undefined)
                }
                emoji={testIllustration ? undefined : item.icon}
                imageSource={
                  productBody?.imageUrl
                    ? { uri: productBody.imageUrl }
                    : testIllustration
                      ? testIllustration
                      : undefined
                }
                visual={
                  localProductImage ? (
                    <Image
                      source={localProductImage}
                      style={{ width: 248, height: 138 }}
                      resizeMode="contain"
                    />
                  ) : undefined
                }
                imageSize={testIllustration ? 72 : undefined}
                badges={badges}
              />

              {/* ── Content sections ── */}
              {isProduct ? (
                <ProductSections
                  body={item.body as ProductBody}
                  productId={item.id}
                />
              ) : (
                <TestSections
                  id={item.id}
                  body={item.body as unknown as TestBody}
                  sources={item.sources}
                  onEvidenceLevelPress={setEvidenceModal}
                  onOpenMetric={onOpenMetric}
                  onOpenFactSheet={onOpenFactSheet}
                />
              )}

              {/* ── Disclaimer ── */}
              <View
                style={{
                  marginTop: 32,
                  padding: 14,
                  borderRadius: 12,
                  backgroundColor: "rgba(0,0,0,0.03)",
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 11,
                    color: EMBER.colors.textFaint,
                    lineHeight: 17,
                    textAlign: "center",
                  }}
                >
                  {t("learn.disclaimer_medical_info")}
                </Text>
              </View>
            </View>
          </Animated.ScrollView>
        </View>
      </Animated.View>
      <EvidenceLevelModal
        level={evidenceModal}
        visible={evidenceModal !== null}
        onClose={() => setEvidenceModal(null)}
      />
    </View>
  );
}

function splitTestSteps(text: string): string[] {
  return text
    .split(/(?<=[.!?])\s+/)
    .map((step) => step.trim())
    .filter(Boolean);
}

function TestStepRows({
  items,
  currentBlockId,
  onOpenFactSheet,
}: {
  items: string[];
  currentBlockId: string;
  onOpenFactSheet?: (target: FactSheetLinkTarget) => void;
}) {
  if (!items.length) return null;

  return (
    <View
      style={{
        borderRadius: 8,
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.06)",
        backgroundColor: "rgba(255,255,255,0.28)",
        overflow: "hidden",
        marginTop: 10,
      }}
    >
      {items.map((item, i) => (
        <View
          key={`${item}-${i}`}
          style={{
            flexDirection: "row",
            gap: 10,
            paddingHorizontal: 12,
            paddingVertical: 12,
            borderTopWidth: i === 0 ? 0 : 1,
            borderTopColor: "rgba(0,0,0,0.05)",
          }}
        >
          <View
            style={{
              width: 22,
              height: 22,
              borderRadius: 11,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(88, 78, 68, 0.10)",
              marginTop: 1,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                fontWeight: "700",
                color: EMBER.colors.textMid,
              }}
            >
              {i + 1}
            </Text>
          </View>
          <LinkedFactSheetText
            text={item}
            currentBlockId={currentBlockId}
            onOpenLink={onOpenFactSheet}
            style={{
              flex: 1,
              fontSize: 13,
              color: EMBER.colors.textLight,
              lineHeight: 18,
            }}
          />
        </View>
      ))}
    </View>
  );
}

function ReadableLinkedFactSheetText({
  text,
  currentBlockId,
  onOpenFactSheet,
  style,
}: {
  text: string;
  currentBlockId: string;
  onOpenFactSheet?: (target: FactSheetLinkTarget) => void;
  style?: StyleProp<TextStyle>;
}) {
  const paragraphs = text
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  if (!paragraphs.length) return null;

  return (
    <View style={{ gap: 10, marginTop: 10 }}>
      {paragraphs.map((paragraph, index) => (
        <LinkedFactSheetText
          key={`${paragraph.slice(0, 32)}-${index}`}
          text={paragraph}
          currentBlockId={currentBlockId}
          onOpenLink={onOpenFactSheet}
          style={style}
        />
      ))}
    </View>
  );
}

function BenefitRows({ items }: { items: string[] }) {
  if (!items.length) return null;

  return (
    <View style={{ gap: 10, marginTop: 10 }}>
      {items.map((item, i) => (
        <View
          key={`${item}-${i}`}
          style={{
            flexDirection: "row",
            gap: 10,
            paddingHorizontal: 12,
            paddingVertical: 12,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: "rgba(0,0,0,0.06)",
            backgroundColor: "rgba(255,255,255,0.28)",
          }}
        >
          <View
            style={{
              width: 24,
              height: 24,
              borderRadius: 12,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(86,124,84,0.10)",
              marginTop: 1,
            }}
          >
            <CheckCircle size={13} color={EMBER.colors.green} strokeWidth={2} />
          </View>
          <Text
            className="font-dm-sans"
            style={{
              flex: 1,
              fontSize: 13,
              color: EMBER.colors.textLight,
              lineHeight: 18,
            }}
          >
            {item}
          </Text>
        </View>
      ))}
    </View>
  );
}

function ResearchFindingRows({
  items,
}: {
  items: NonNullable<TestBody["keyFindings"]>;
}) {
  if (!items.length) return null;

  return (
    <View style={{ gap: 12, marginTop: 10 }}>
      {items.map((finding, i) => {
        const DirectionIcon =
          finding.direction === "positive"
            ? TrendingUp
            : finding.direction === "negative"
              ? TrendingDown
              : Minus;
        const directionColor =
          finding.direction === "positive"
            ? EMBER.colors.green
            : finding.direction === "negative"
              ? "#C0392B"
              : EMBER.colors.textLight;

        return (
          <View
            key={`${finding.outcome}-${i}`}
            style={{
              borderRadius: 8,
              borderWidth: 1,
              borderColor: "rgba(0,0,0,0.06)",
              backgroundColor: "rgba(255,255,255,0.28)",
              paddingHorizontal: 12,
              paddingVertical: 12,
            }}
          >
            <View style={{ flexDirection: "row", gap: 10 }}>
              <View
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 12,
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: `${directionColor}14`,
                  marginTop: 1,
                }}
              >
                <DirectionIcon
                  size={12}
                  color={directionColor}
                  strokeWidth={2.5}
                />
              </View>
              <Text
                className="font-dm-sans"
                style={{
                  flex: 1,
                  fontSize: 14,
                  fontWeight: "600",
                  color: EMBER.colors.textMid,
                  lineHeight: 20,
                }}
              >
                {finding.outcome}
              </Text>
            </View>

            {finding.magnitude ? (
              <View
                style={{
                  marginTop: 8,
                  marginLeft: 34,
                  paddingHorizontal: 10,
                  paddingVertical: 5,
                  borderRadius: 8,
                  backgroundColor: `${directionColor}0A`,
                  alignSelf: "flex-start",
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    fontWeight: "700",
                    color: directionColor,
                  }}
                >
                  {finding.magnitude}
                </Text>
              </View>
            ) : null}

            {finding.mechanism ? (
              <Text
                className="font-dm-sans"
                style={{
                  marginTop: 8,
                  marginLeft: 34,
                  fontSize: 13,
                  color: EMBER.colors.textLight,
                  lineHeight: 19,
                }}
              >
                {finding.mechanism}
              </Text>
            ) : null}

            {finding.source ? (
              <Text
                className="font-dm-sans"
                style={{
                  marginTop: 6,
                  marginLeft: 34,
                  fontSize: 11,
                  color: EMBER.colors.textFaint,
                  lineHeight: 16,
                }}
              >
                {finding.source}
              </Text>
            ) : null}
          </View>
        );
      })}
    </View>
  );
}

type TestQuickFact = NonNullable<TestBody["quickFacts"]>[number];
type CommercialProductProfile = NonNullable<
  TestBody["commercialProductProfile"]
>;

const QUICK_FACT_LABELS: Record<TestQuickFact["kind"], string> = {
  sample: "Sample",
  time: "Time",
  pain: "Pain",
  anesthesia: "Anesthesia",
  prep: "Prep",
  results: "Results",
};

const QUICK_FACT_ICONS = {
  sample: Beaker,
  time: Timer,
  pain: Activity,
  anesthesia: Moon,
  prep: ClipboardCheck,
  results: FileText,
} as const;

function TestQuickFacts({ items }: { items: TestQuickFact[] }) {
  if (!items.length) return null;

  return (
    <View
      style={{
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 10,
        marginTop: 10,
      }}
    >
      {items.map((item, index) => {
        const Icon = QUICK_FACT_ICONS[item.kind];
        const label = item.label ?? QUICK_FACT_LABELS[item.kind];

        return (
          <View
            key={`${item.kind}-${index}`}
            style={{
              flexBasis: "47%",
              flexGrow: 1,
              minWidth: 140,
              minHeight: 82,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: "rgba(0,0,0,0.06)",
              backgroundColor: "rgba(255,255,255,0.28)",
              paddingHorizontal: 12,
              paddingVertical: 12,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 7,
                marginBottom: 8,
              }}
            >
              <View
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 12,
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(88, 78, 68, 0.10)",
                }}
              >
                <Icon size={13} color={EMBER.colors.textMid} strokeWidth={2} />
              </View>
              <Text
                className="font-dm-sans"
                style={{
                  flex: 1,
                  ...MINI_LABEL_TEXT_STYLE,
                }}
              >
                {label}
              </Text>
            </View>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                fontWeight: "600",
                color: EMBER.colors.textMid,
                lineHeight: 18,
              }}
            >
              {item.value}
            </Text>
          </View>
        );
      })}
    </View>
  );
}

function CommercialProductProfileRows({
  profile,
}: {
  profile: CommercialProductProfile;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("CommercialProductProfileRows");
  const { locale } = useLocale();
  const hasNames = Boolean(profile.marketedNames?.length);
  const hasAvailability = Boolean(profile.availability?.length);

  if (!hasNames && !hasAvailability && !profile.productUrl) return null;

  const rows = [
    ...(hasNames
      ? [
          {
            key: "marketed-names",
            icon: Tags,
            label: t("learn.test_detail.marketed_as"),
            detail: profile.marketedNames!.join(", "),
            countries: undefined,
          },
        ]
      : []),
    ...(profile.availability ?? []).map((item, index) => ({
      key: `availability-${index}`,
      icon: Globe2,
      label: item.region,
      detail: item.detail,
      countries: item.countries,
    })),
  ];

  return (
    <View style={{ gap: 10, marginTop: 10 }}>
      {rows.map((row) => {
        const Icon = row.icon;

        return (
          <View
            key={row.key}
            style={{
              flexDirection: "row",
              gap: 10,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: "rgba(0,0,0,0.06)",
              backgroundColor: "rgba(255,255,255,0.28)",
              paddingHorizontal: 12,
              paddingVertical: 12,
            }}
          >
            <View
              style={{
                width: 24,
                height: 24,
                borderRadius: 12,
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(88, 78, 68, 0.10)",
                marginTop: 1,
              }}
            >
              <Icon size={13} color={EMBER.colors.textMid} strokeWidth={2} />
            </View>
            <View style={{ flex: 1, gap: 3 }}>
              <Text className="font-dm-sans" style={MINI_LABEL_TEXT_STYLE}>
                {row.label}
              </Text>
              {row.countries?.length ? (
                <View
                  style={{
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 6,
                    marginTop: 2,
                    marginBottom: 3,
                  }}
                >
                  {row.countries.map((country) => {
                    const flag = flagFromIso(country);
                    return (
                      <View
                        key={country}
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 4,
                          borderRadius: 999,
                          backgroundColor: "rgba(88, 78, 68, 0.09)",
                          paddingHorizontal: 8,
                          paddingVertical: 4,
                        }}
                      >
                        {flag ? (
                          <Text
                            className="font-dm-sans"
                            style={{ fontSize: 12, lineHeight: 14 }}
                          >
                            {flag}
                          </Text>
                        ) : null}
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 11,
                            fontWeight: "700",
                            color: EMBER.colors.textMid,
                            lineHeight: 14,
                          }}
                        >
                          {getCountryLabel(country, locale)}
                        </Text>
                      </View>
                    );
                  })}
                </View>
              ) : null}
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  color: EMBER.colors.textLight,
                  lineHeight: 18,
                }}
              >
                {row.detail}
              </Text>
            </View>
          </View>
        );
      })}

      {profile.productUrl ? (
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel("Official product page"),
              "open_product_page",
            );
            Linking.openURL(profile.productUrl!).catch(() => {});
          }}
          style={({ pressed }) => ({
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: "rgba(0,0,0,0.06)",
            backgroundColor: pressed
              ? "rgba(0,0,0,0.04)"
              : "rgba(255,255,255,0.28)",
            paddingHorizontal: 12,
            paddingVertical: 12,
          })}
        >
          <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
            <View
              style={{
                width: 24,
                height: 24,
                borderRadius: 12,
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(88, 78, 68, 0.10)",
              }}
            >
              <ExternalLink
                size={13}
                color={EMBER.colors.textMid}
                strokeWidth={2}
              />
            </View>
            <Text
              className="font-dm-sans"
              style={{
                flex: 1,
                fontSize: 13,
                fontWeight: "700",
                color: EMBER.colors.textMid,
                lineHeight: 18,
              }}
            >
              Official product page
            </Text>
          </View>
          <ChevronRight
            size={16}
            color={EMBER.colors.textFaint}
            strokeWidth={2}
          />
        </Pressable>
      ) : null}
    </View>
  );
}

function CommercialProductProfileSection({
  profile,
}: {
  profile: CommercialProductProfile;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("CommercialProductProfileSection");
  const hasContent = Boolean(
    profile.marketedNames?.length ||
    profile.availability?.length ||
    profile.productUrl,
  );
  const [expanded, setExpanded] = useState(false);

  if (!hasContent) return null;

  return (
    <View style={{ marginTop: 24 }}>
      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(t("learn.test_detail.section_available_as")),
            expanded ? "collapse_available_as" : "expand_available_as",
          );
          setExpanded((current) => !current);
        }}
        accessibilityRole="button"
        accessibilityLabel={t("learn.test_detail.section_available_as")}
        style={({ pressed }) => ({
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          opacity: pressed ? 0.72 : 1,
        })}
      >
        <SectionTitle icon={Globe2}>
          {t("learn.test_detail.section_available_as")}
        </SectionTitle>
        {expanded ? (
          <ChevronUp size={16} color={EMBER.colors.textLight} strokeWidth={2} />
        ) : (
          <ChevronDown
            size={16}
            color={EMBER.colors.textLight}
            strokeWidth={2}
          />
        )}
      </Pressable>
      <Collapsible expanded={expanded}>
        <CommercialProductProfileRows profile={profile} />
      </Collapsible>
    </View>
  );
}

function IconTextRows({
  items,
  tone,
}: {
  items: string[];
  tone: "positive" | "caution";
}) {
  if (!items.length) return null;

  const Icon = tone === "positive" ? CheckCircle : XCircle;
  const iconColor = tone === "positive" ? EMBER.colors.green : "#C0392B";

  return (
    <View style={{ gap: 10, marginTop: 10 }}>
      {items.map((item, i) => (
        <View
          key={`${item}-${i}`}
          style={{
            flexDirection: "row",
            gap: 10,
            paddingHorizontal: 12,
            paddingVertical: 12,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: "rgba(0,0,0,0.06)",
            backgroundColor: "rgba(255,255,255,0.28)",
          }}
        >
          <View
            style={{
              width: 24,
              height: 24,
              borderRadius: 12,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: `${iconColor}12`,
              marginTop: 1,
            }}
          >
            <Icon size={13} color={iconColor} strokeWidth={2} />
          </View>
          <Text
            className="font-dm-sans"
            style={{
              flex: 1,
              fontSize: 13,
              color: EMBER.colors.textLight,
              lineHeight: 18,
            }}
          >
            {item}
          </Text>
        </View>
      ))}
    </View>
  );
}

function PlainTextRows({
  items,
  icon: Icon,
}: {
  items: string[];
  icon: typeof AlertCircle;
}) {
  if (!items.length) return null;

  return (
    <View style={{ gap: 10, marginTop: 10 }}>
      {items.map((item, i) => (
        <View
          key={`${item}-${i}`}
          style={{
            flexDirection: "row",
            gap: 10,
            paddingHorizontal: 12,
            paddingVertical: 12,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: "rgba(0,0,0,0.06)",
            backgroundColor: "rgba(255,255,255,0.28)",
          }}
        >
          <View
            style={{
              width: 24,
              height: 24,
              borderRadius: 12,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(88, 78, 68, 0.10)",
              marginTop: 1,
            }}
          >
            <Icon size={13} color={EMBER.colors.textMid} strokeWidth={2} />
          </View>
          <Text
            className="font-dm-sans"
            style={{
              flex: 1,
              fontSize: 13,
              color: EMBER.colors.textLight,
              lineHeight: 18,
            }}
          >
            {item}
          </Text>
        </View>
      ))}
    </View>
  );
}

function BestForRows({ items }: { items: string[] }) {
  if (!items.length) return null;

  return (
    <View style={{ gap: 8, marginTop: 10 }}>
      {items.map((item, i) => (
        <View
          key={`${item}-${i}`}
          style={{
            flexDirection: "row",
            alignItems: "flex-start",
            gap: 10,
            paddingHorizontal: 12,
            paddingVertical: 10,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: "rgba(0,0,0,0.06)",
            backgroundColor: "rgba(255,255,255,0.28)",
            width: "100%",
          }}
        >
          <View
            style={{
              width: 22,
              height: 22,
              borderRadius: 11,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(88, 78, 68, 0.10)",
              marginTop: 1,
            }}
          >
            <Target size={12} color={EMBER.colors.textMid} strokeWidth={2} />
          </View>
          <Text
            className="font-dm-sans"
            style={{
              flex: 1,
              flexShrink: 1,
              fontSize: 13,
              color: EMBER.colors.textLight,
              lineHeight: 18,
            }}
          >
            {item}
          </Text>
        </View>
      ))}
    </View>
  );
}

function MarkerTimingRows({
  items,
}: {
  items: NonNullable<TestBody["markerTiming"]>;
}) {
  if (!items.length) return null;

  return (
    <View style={{ gap: 10, marginTop: 10 }}>
      {items.map((item, i) => (
        <View
          key={`${item.marker}-${i}`}
          style={{
            flexDirection: "row",
            gap: 12,
            paddingHorizontal: 12,
            paddingVertical: 12,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: "rgba(0,0,0,0.06)",
            backgroundColor: "rgba(255,255,255,0.28)",
          }}
        >
          <View
            style={{
              width: 30,
              height: 30,
              borderRadius: 15,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(88, 78, 68, 0.10)",
              marginTop: 1,
            }}
          >
            <CalendarDays
              size={15}
              color={EMBER.colors.textMid}
              strokeWidth={2}
            />
          </View>
          <View style={{ flex: 1, gap: 5 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                fontWeight: "700",
                color: EMBER.colors.textMid,
                lineHeight: 18,
              }}
            >
              {item.marker}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                fontWeight: "600",
                color: EMBER.colors.textLight,
                lineHeight: 18,
              }}
            >
              {item.timing}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                color: EMBER.colors.textFaint,
                lineHeight: 17,
              }}
            >
              {item.reason}
            </Text>
          </View>
        </View>
      ))}
    </View>
  );
}

const RESULT_GLYPH_COLORS = {
  walnut: "#8A6F5B",
  taupe: "#B99A83",
  gold: "#D9A441",
  copper: "#C87952",
  clay: "#E0A98C",
  pale: "#D8D1C8",
  faint: "rgba(184,166,149,0.38)",
  ink: "#6F5949",
};

function ResultGlyphPart({ style }: { style: ViewStyle }) {
  return (
    <View
      style={[
        {
          position: "absolute",
          borderRadius: 999,
        },
        style,
      ]}
    />
  );
}

function UnknownResultGlyph() {
  return (
    <View
      style={{
        width: 42,
        height: 42,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <ResultGlyphPart
        style={{
          width: 28,
          height: 28,
          borderWidth: 2,
          borderColor: RESULT_GLYPH_COLORS.pale,
          backgroundColor: "transparent",
        }}
      />
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 20,
          fontWeight: "800",
          color: RESULT_GLYPH_COLORS.taupe,
          lineHeight: 24,
        }}
      >
        ?
      </Text>
    </View>
  );
}

function WindowResultGlyph({ kind }: { kind: ResultGlyphKind }) {
  const activeIndex =
    kind === "window-pre" ? 0 : kind === "window-post" ? 2 : 1;
  const activeColor =
    kind === "window-receptive"
      ? RESULT_GLYPH_COLORS.gold
      : RESULT_GLYPH_COLORS.copper;

  return (
    <View style={{ width: 42, height: 42 }}>
      <ResultGlyphPart
        style={{
          left: 6,
          top: 31,
          width: 30,
          height: 3,
          backgroundColor: RESULT_GLYPH_COLORS.faint,
        }}
      />
      {[0, 1, 2].map((index) => {
        const isActive = index === activeIndex;
        return (
          <ResultGlyphPart
            key={index}
            style={{
              left: 9 + index * 10,
              top: isActive ? 8 : 14,
              width: 6,
              height: isActive ? 24 : 18,
              backgroundColor: isActive
                ? activeColor
                : RESULT_GLYPH_COLORS.pale,
            }}
          />
        );
      })}
      <ResultGlyphPart
        style={{
          left: 17,
          top: 5,
          width: 8,
          height: 8,
          backgroundColor:
            kind === "window-receptive"
              ? RESULT_GLYPH_COLORS.clay
              : RESULT_GLYPH_COLORS.faint,
        }}
      />
    </View>
  );
}

function MicrobiomeResultGlyph({ kind }: { kind: ResultGlyphKind }) {
  const isDominant = kind === "microbiome-dominant";
  const isPathogen = kind === "microbiome-pathogen";
  const isLow = kind === "microbiome-low";
  const rods = [
    {
      left: 8,
      top: 10,
      width: 15,
      height: 5,
      rotate: "-22deg",
      color: isPathogen ? RESULT_GLYPH_COLORS.copper : RESULT_GLYPH_COLORS.gold,
      opacity: isLow ? 0.42 : 1,
    },
    {
      left: 21,
      top: 8,
      width: 12,
      height: 5,
      rotate: "28deg",
      color: isDominant
        ? RESULT_GLYPH_COLORS.taupe
        : RESULT_GLYPH_COLORS.copper,
      opacity: isLow ? 0.32 : 1,
    },
    {
      left: 11,
      top: 23,
      width: 13,
      height: 5,
      rotate: "18deg",
      color: isDominant ? RESULT_GLYPH_COLORS.taupe : RESULT_GLYPH_COLORS.clay,
      opacity: isLow ? 0.28 : 1,
    },
    {
      left: 25,
      top: 25,
      width: 9,
      height: 5,
      rotate: "-34deg",
      color: isDominant ? RESULT_GLYPH_COLORS.gold : RESULT_GLYPH_COLORS.walnut,
      opacity: isLow ? 0 : 1,
    },
  ];

  return (
    <View style={{ width: 42, height: 42 }}>
      {rods.map((rod, index) =>
        rod.opacity === 0 ? null : (
          <ResultGlyphPart
            key={index}
            style={{
              left: rod.left,
              top: rod.top,
              width: rod.width,
              height: rod.height,
              backgroundColor: rod.color,
              opacity: rod.opacity,
              transform: [{ rotate: rod.rotate }],
            }}
          />
        ),
      )}
      <ResultGlyphPart
        style={{
          left: 15,
          top: 16,
          width: 6,
          height: 6,
          backgroundColor: isPathogen
            ? RESULT_GLYPH_COLORS.copper
            : RESULT_GLYPH_COLORS.pale,
          opacity: isLow ? 0.35 : 1,
        }}
      />
      {isLow ? (
        <Text
          className="font-dm-sans"
          style={{
            position: "absolute",
            right: 7,
            bottom: 4,
            fontSize: 17,
            fontWeight: "800",
            color: RESULT_GLYPH_COLORS.taupe,
            lineHeight: 20,
          }}
        >
          ?
        </Text>
      ) : null}
    </View>
  );
}

function DnaMini({
  left,
  top = 5,
  colorA,
  colorB,
  scale = 1,
}: {
  left: number;
  top?: number;
  colorA: string;
  colorB: string;
  scale?: number;
}) {
  const dotSize = 4 * scale;
  return (
    <>
      {[0, 1, 2, 3].map((index) => (
        <View key={index}>
          <ResultGlyphPart
            style={{
              left: left + (index % 2) * 9 * scale,
              top: top + index * 7 * scale,
              width: dotSize,
              height: dotSize,
              backgroundColor: index % 2 === 0 ? colorA : colorB,
            }}
          />
          <ResultGlyphPart
            style={{
              left: left + 10 * scale - (index % 2) * 9 * scale,
              top: top + index * 7 * scale,
              width: dotSize,
              height: dotSize,
              backgroundColor: index % 2 === 0 ? colorB : colorA,
            }}
          />
          <ResultGlyphPart
            style={{
              left: left + 3 * scale,
              top: top + 1.5 * scale + index * 7 * scale,
              width: 11 * scale,
              height: 1.8 * scale,
              backgroundColor: RESULT_GLYPH_COLORS.faint,
              transform: [{ rotate: index % 2 === 0 ? "18deg" : "-18deg" }],
            }}
          />
        </View>
      ))}
    </>
  );
}

function GeneticResultGlyph({ kind }: { kind: ResultGlyphKind }) {
  if (kind === "genetic-pair-risk") {
    return (
      <View style={{ width: 42, height: 42 }}>
        <DnaMini
          left={5}
          colorA={RESULT_GLYPH_COLORS.copper}
          colorB={RESULT_GLYPH_COLORS.gold}
          scale={0.82}
        />
        <DnaMini
          left={22}
          colorA={RESULT_GLYPH_COLORS.copper}
          colorB={RESULT_GLYPH_COLORS.gold}
          scale={0.82}
        />
      </View>
    );
  }

  const colorA =
    kind === "genetic-affected"
      ? RESULT_GLYPH_COLORS.copper
      : RESULT_GLYPH_COLORS.gold;
  const colorB =
    kind === "genetic-clear"
      ? RESULT_GLYPH_COLORS.taupe
      : RESULT_GLYPH_COLORS.copper;

  return (
    <View style={{ width: 42, height: 42 }}>
      <DnaMini left={11} colorA={colorA} colorB={colorB} scale={1.1} />
    </View>
  );
}

function ChromosomeX({
  left,
  top,
  color,
  shortArm,
  opacity = 1,
}: {
  left: number;
  top: number;
  color: string;
  shortArm?: boolean;
  opacity?: number;
}) {
  return (
    <>
      <ResultGlyphPart
        style={{
          left,
          top,
          width: shortArm ? 4 : 5,
          height: 24,
          backgroundColor: color,
          opacity,
          transform: [{ rotate: "24deg" }],
        }}
      />
      <ResultGlyphPart
        style={{
          left: left + 8,
          top: top + (shortArm ? 5 : 0),
          width: 5,
          height: shortArm ? 16 : 24,
          backgroundColor: color,
          opacity,
          transform: [{ rotate: "-24deg" }],
        }}
      />
    </>
  );
}

function ChromosomeResultGlyph({ kind }: { kind: ResultGlyphKind }) {
  if (kind === "chromosome-mosaic") {
    return (
      <View style={{ width: 42, height: 42 }}>
        <ChromosomeX
          left={7}
          top={9}
          color={RESULT_GLYPH_COLORS.taupe}
          opacity={0.9}
        />
        <ChromosomeX
          left={21}
          top={9}
          color={RESULT_GLYPH_COLORS.copper}
          opacity={0.9}
        />
      </View>
    );
  }

  const color =
    kind === "chromosome-unbalanced"
      ? RESULT_GLYPH_COLORS.copper
      : kind === "chromosome-balanced"
        ? RESULT_GLYPH_COLORS.gold
        : RESULT_GLYPH_COLORS.taupe;

  return (
    <View style={{ width: 42, height: 42 }}>
      <ChromosomeX
        left={8}
        top={9}
        color={color}
        shortArm={kind === "chromosome-unbalanced"}
      />
      <ChromosomeX
        left={22}
        top={9}
        color={
          kind === "chromosome-balanced" ? RESULT_GLYPH_COLORS.taupe : color
        }
      />
    </View>
  );
}

function ResultTypeGlyph({ kind }: { kind: ResultGlyphKind }) {
  if (kind.startsWith("window-")) return <WindowResultGlyph kind={kind} />;
  if (kind.startsWith("microbiome-")) {
    return <MicrobiomeResultGlyph kind={kind} />;
  }
  if (kind.startsWith("genetic-")) return <GeneticResultGlyph kind={kind} />;
  if (kind.startsWith("chromosome-")) {
    return <ChromosomeResultGlyph kind={kind} />;
  }
  return <UnknownResultGlyph />;
}

function ResultTypeRows({
  items,
  illustrations,
  glyphs,
}: {
  items: NonNullable<TestBody["resultTypes"]>;
  illustrations?: Partial<Record<string, ImageSourcePropType>>;
  glyphs?: Partial<Record<string, ResultGlyphKind>>;
}) {
  if (!items.length) return null;

  return (
    <View style={{ gap: 10, marginTop: 10 }}>
      {items.map((item) => {
        const illustration = illustrations?.[item.label];
        const glyph = glyphs?.[item.label];
        const hasVisual = Boolean(illustration || glyph);

        return (
          <View
            key={item.label}
            style={{
              paddingHorizontal: 12,
              paddingVertical: 12,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: "rgba(0,0,0,0.06)",
              backgroundColor: "rgba(255,255,255,0.28)",
            }}
          >
            <View
              style={{
                gap: 7,
              }}
            >
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: hasVisual ? 12 : 0,
                }}
              >
                {illustration ? (
                  <Image
                    source={illustration}
                    style={{
                      width: 42,
                      height: 42,
                    }}
                    resizeMode="contain"
                  />
                ) : glyph ? (
                  <ResultTypeGlyph kind={glyph} />
                ) : null}
                <View style={{ flex: 1, gap: 3 }}>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 14,
                      fontWeight: "800",
                      color: EMBER.colors.textMid,
                      lineHeight: 18,
                    }}
                  >
                    {item.label}
                  </Text>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 13,
                      color: EMBER.colors.textLight,
                      lineHeight: 18,
                    }}
                  >
                    {item.meaning}
                  </Text>
                </View>
              </View>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  color: EMBER.colors.textFaint,
                  lineHeight: 17,
                }}
              >
                {item.whatToExpect}
              </Text>
            </View>
          </View>
        );
      })}
    </View>
  );
}

function SoldAsRows({
  items,
  onOpenFactSheet,
}: {
  items: NonNullable<TestBody["soldAs"]>;
  onOpenFactSheet?: (target: FactSheetLinkTarget) => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("SoldAsRows");
  if (!items.length) return null;

  return (
    <View style={{ gap: 12, marginTop: 10 }}>
      {items.map((item) => {
        const canOpen = Boolean(item.linkedTestId && onOpenFactSheet);
        const content = (
          <>
            <View style={{ gap: 11 }}>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 6,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 14,
                    fontWeight: "800",
                    color: EMBER.colors.textMid,
                    lineHeight: 18,
                  }}
                >
                  {item.name}
                </Text>
                {item.company ? (
                  <View
                    style={{
                      paddingHorizontal: 8,
                      paddingVertical: 3,
                      borderRadius: 999,
                      backgroundColor: "rgba(88, 78, 68, 0.10)",
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 10,
                        fontWeight: "700",
                        color: EMBER.colors.textMid,
                        lineHeight: 13,
                      }}
                    >
                      {item.company}
                    </Text>
                  </View>
                ) : null}
              </View>
              <View style={{ gap: 4 }}>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 10,
                    fontWeight: "800",
                    color: EMBER.colors.textFaint,
                    lineHeight: 13,
                    textTransform: "uppercase",
                    letterSpacing: 0,
                  }}
                >
                  {t("learn.test_detail.what_it_is")}
                </Text>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    color: EMBER.colors.textLight,
                    lineHeight: 17,
                  }}
                >
                  {item.description}
                </Text>
              </View>
              {item.evidenceSummary ? (
                <View style={{ gap: 4 }}>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 10,
                      fontWeight: "800",
                      color: EMBER.colors.textFaint,
                      lineHeight: 13,
                      textTransform: "uppercase",
                      letterSpacing: 0,
                    }}
                  >
                    {t("learn.test_detail.evidence_note")}
                  </Text>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 11,
                      color: EMBER.colors.textFaint,
                      lineHeight: 16,
                    }}
                  >
                    {item.evidenceSummary}
                  </Text>
                </View>
              ) : null}
            </View>
            {canOpen ? (
              <View
                style={{
                  marginTop: 12,
                  paddingTop: 10,
                  borderTopWidth: 1,
                  borderTopColor: "rgba(0,0,0,0.055)",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 11,
                    fontWeight: "800",
                    color: EMBER.colors.textMid,
                    lineHeight: 14,
                  }}
                >
                  View product evidence
                </Text>
                <ChevronRight
                  size={16}
                  color={EMBER.colors.textFaint}
                  strokeWidth={2}
                />
              </View>
            ) : null}
          </>
        );

        const sharedStyle = {
          paddingHorizontal: 12,
          paddingVertical: 14,
          borderRadius: 8,
          borderWidth: 1,
          borderColor: "rgba(0,0,0,0.06)",
        };

        return canOpen ? (
          <Pressable
            key={item.name}
            onPress={() => {
              capturePress(pressLabel(item.name), "open_sold_as_test");
              onOpenFactSheet?.({
                kind: "test",
                id: item.linkedTestId as string,
              });
            }}
            style={({ pressed }) => ({
              ...sharedStyle,
              backgroundColor: pressed
                ? "rgba(0,0,0,0.04)"
                : "rgba(255,255,255,0.28)",
            })}
          >
            {content}
          </Pressable>
        ) : (
          <View
            key={item.name}
            style={{
              ...sharedStyle,
              backgroundColor: "rgba(255,255,255,0.28)",
            }}
          >
            {content}
          </View>
        );
      })}
    </View>
  );
}

function TestDataPointRows({
  dataPointIds,
  onOpenMetric,
}: {
  dataPointIds: ContentBlockId[];
  onOpenMetric?: (metricKey: string) => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("TestDataPointRows");
  const dataPoints = dataPointIds
    .map((id) => getBlock(id))
    .filter(
      (block): block is ContentBlock<"data-point"> =>
        block !== undefined && block.kind === "data-point",
    );
  if (!dataPoints.length) return null;

  const rowBaseStyle = {
    flexDirection: "row" as const,
    alignItems: "center" as const,
    gap: 12,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.06)",
  };

  return (
    <View style={{ marginTop: 24 }}>
      <SectionTitle icon={Beaker}>
        {t("learn.test_detail.section_biomarkers_tracked")}
      </SectionTitle>
      <View style={{ marginTop: 10, gap: 8 }}>
        {dataPoints.map((dataPoint) => {
          const body = dataPoint.body as DataPointBody;
          const canOpen = Boolean(body.metricKey && onOpenMetric);
          const content = (
            <>
              <View
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 17,
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(0,0,0,0.045)",
                }}
              >
                <Beaker size={16} color={EMBER.colors.textMid} />
              </View>
              <View style={{ flex: 1, gap: 6 }}>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 14,
                    fontWeight: "700",
                    color: EMBER.colors.textMid,
                    lineHeight: 18,
                  }}
                >
                  {dataPoint.title}
                </Text>
                {body.interpretation ? (
                  <Text
                    className="font-dm-sans"
                    numberOfLines={2}
                    style={{
                      fontSize: 12,
                      color: EMBER.colors.textLight,
                      lineHeight: 17,
                    }}
                  >
                    {body.interpretation}
                  </Text>
                ) : null}
                <View
                  style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}
                >
                  {body.sampleTypes?.map((sampleType) => (
                    <FactSheetBadge
                      key={sampleType}
                      label={getSampleTypeLabel(sampleType)}
                    />
                  ))}
                  {body.unit ? <FactSheetBadge label={body.unit} /> : null}
                </View>
              </View>
              {canOpen ? (
                <ChevronRight size={16} color={EMBER.colors.textFaint} />
              ) : null}
            </>
          );

          return canOpen ? (
            <Pressable
              key={dataPoint.id}
              onPress={() => {
                capturePress(pressLabel(dataPoint.title), "open_metric");
                onOpenMetric?.(body.metricKey!);
              }}
              style={({ pressed }) => ({
                ...rowBaseStyle,
                backgroundColor: pressed
                  ? "rgba(0,0,0,0.04)"
                  : "rgba(255,255,255,0.28)",
              })}
            >
              {content}
            </Pressable>
          ) : (
            <View
              key={dataPoint.id}
              style={{
                ...rowBaseStyle,
                backgroundColor: "rgba(255,255,255,0.28)",
              }}
            >
              {content}
            </View>
          );
        })}
      </View>
    </View>
  );
}

/* ─── Test detail sections (existing layout) ─── */

function TestSections({
  id,
  body,
  sources,
  onEvidenceLevelPress,
  onOpenMetric,
  onOpenFactSheet,
}: {
  id: ContentBlockId;
  body: TestBody;
  sources?: ContentBlock["sources"];
  onEvidenceLevelPress: (level: string) => void;
  onOpenMetric?: (metricKey: string) => void;
  onOpenFactSheet?: (target: FactSheetLinkTarget) => void;
}) {
  const t = useTranslations();
  const howItWorksSteps =
    body.howItWorksSteps ?? splitTestSteps(body.howItWorks);
  const showCycleDaySection = Boolean(body.cycleDay && !body.howItWorksSteps);
  const hasResultsQuickFact =
    body.quickFacts?.some((fact) => fact.kind === "results") ?? false;
  const considerationItems = [
    ...(body.expectations?.shouldNotExpect ?? []),
    ...(body.considerations ?? []),
  ];
  return (
    <>
      <View style={{ marginTop: 28 }}>
        <SectionTitle icon={Heart}>
          {t("learn.test_detail.section_what_it_measures")}
        </SectionTitle>
        <ReadableLinkedFactSheetText
          text={body.whatItMeasures}
          currentBlockId={id}
          onOpenFactSheet={onOpenFactSheet}
          style={{
            fontSize: 14,
            color: EMBER.colors.textLight,
            lineHeight: 22,
          }}
        />
      </View>

      {body.quickFacts?.length ? (
        <View style={{ marginTop: 24 }}>
          <SectionTitle icon={Info}>
            {t("learn.test_detail.section_at_a_glance")}
          </SectionTitle>
          <TestQuickFacts items={body.quickFacts} />
        </View>
      ) : null}

      {body.soldAs?.length ? (
        <View style={{ marginTop: 24 }}>
          <SectionTitle icon={ExternalLink}>
            {t("learn.test_detail.section_sold_as")}
          </SectionTitle>
          <SoldAsRows items={body.soldAs} onOpenFactSheet={onOpenFactSheet} />
        </View>
      ) : null}

      {body.resultTypes?.length ? (
        <View style={{ marginTop: 24 }}>
          <SectionTitle icon={FileText}>
            {t("learn.test_detail.section_result_types")}
          </SectionTitle>
          <ResultTypeRows
            items={body.resultTypes}
            illustrations={TEST_RESULT_TYPE_ILLUSTRATIONS[id]}
            glyphs={TEST_RESULT_TYPE_GLYPHS[id]}
          />
        </View>
      ) : null}

      <TestDataPointRows
        dataPointIds={getDataPointsForTest(id).map((dataPoint) => dataPoint.id)}
        onOpenMetric={onOpenMetric}
      />

      {body.markerTiming?.length ? (
        <View style={{ marginTop: 24 }}>
          <SectionTitle icon={CalendarDays}>
            {t("learn.test_detail.section_marker_timing")}
          </SectionTitle>
          <MarkerTimingRows items={body.markerTiming} />
        </View>
      ) : null}

      <View style={{ marginTop: 24 }}>
        <SectionTitle icon={Wrench}>
          {t("learn.test_detail.section_how_it_works")}
        </SectionTitle>
        <TestStepRows
          items={howItWorksSteps}
          currentBlockId={id}
          onOpenFactSheet={onOpenFactSheet}
        />
      </View>

      {body.bestFor?.length ? (
        <View style={{ marginTop: 24 }}>
          <SectionTitle icon={Target}>
            {t("learn.test_detail.section_best_for")}
          </SectionTitle>
          <BestForRows items={body.bestFor} />
        </View>
      ) : null}

      {body.keyFindings?.length ? (
        <View style={{ marginTop: 24 }}>
          <SectionTitle icon={Beaker}>
            {t("learn.test_detail.section_research")}
          </SectionTitle>
          <ResearchFindingRows items={body.keyFindings} />
        </View>
      ) : null}

      {body.expectations?.canExpect.length ? (
        <View style={{ marginTop: 24 }}>
          <SectionTitle icon={Lightbulb}>
            {t("learn.test_detail.section_what_to_expect")}
          </SectionTitle>
          <IconTextRows items={body.expectations.canExpect} tone="positive" />
        </View>
      ) : null}

      {considerationItems.length ? (
        <View style={{ marginTop: 24 }}>
          <SectionTitle icon={AlertCircle}>
            {t("learn.test_detail.section_considerations")}
          </SectionTitle>
          <PlainTextRows items={considerationItems} icon={AlertCircle} />
        </View>
      ) : null}

      {body.whyItMatters?.length ? (
        <View style={{ marginTop: 24 }}>
          <SectionTitle icon={Lightbulb}>
            {t("learn.test_detail.section_why_it_matters")}
          </SectionTitle>
          <BenefitRows items={body.whyItMatters} />
        </View>
      ) : null}

      {body.turnaroundTime && !hasResultsQuickFact && (
        <View style={{ marginTop: 24 }}>
          <SectionTitle icon={Clock}>
            {t("learn.test_detail.section_turnaround_time")}
          </SectionTitle>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              color: EMBER.colors.textLight,
              lineHeight: 22,
              marginTop: 10,
            }}
          >
            {body.turnaroundTime}
          </Text>
        </View>
      )}

      {sources?.length ? (
        <View style={{ marginTop: 24 }}>
          <SectionTitle icon={BookOpen}>
            {t("learn.test_detail.section_evidence")}
          </SectionTitle>
          <EvidenceSourceRows
            sources={sources}
            onEvidenceLevelPress={onEvidenceLevelPress}
            style={{ marginTop: 10 }}
          />
        </View>
      ) : null}

      {showCycleDaySection ? (
        <View style={{ marginTop: 24 }}>
          <SectionTitle icon={CalendarDays}>
            {t("learn.test_detail.section_when_to_take")}
          </SectionTitle>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              color: EMBER.colors.textLight,
              lineHeight: 22,
              marginTop: 10,
            }}
          >
            {body.cycleDay}
          </Text>
        </View>
      ) : null}

      {body.commercialProductProfile ? (
        <CommercialProductProfileSection
          profile={body.commercialProductProfile}
        />
      ) : null}
    </>
  );
}

/* ─── Product detail sections ─── */

function ProductSections({
  body,
  productId,
}: {
  body: ProductBody;
  productId: ContentBlockId;
}) {
  const t = useTranslations();
  const includedDataPoints = getDataPointsForProduct(productId);
  return (
    <>
      <View style={{ marginTop: 28 }}>
        <SectionTitle icon={Info}>
          {t("learn.test_detail.section_about_product")}
        </SectionTitle>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 14,
            color: EMBER.colors.textLight,
            lineHeight: 22,
            marginTop: 10,
          }}
        >
          {body.description}
        </Text>
      </View>

      {includedDataPoints.length ? (
        <View style={{ marginTop: 24 }}>
          <SectionTitle icon={Beaker}>
            {t("learn.test_detail.section_panel_parameters")}
          </SectionTitle>
          <View style={{ gap: 8, marginTop: 10 }}>
            {includedDataPoints.map((dataPoint) => (
              <Text
                key={dataPoint.id}
                className="font-dm-sans"
                style={{
                  fontSize: 14,
                  color: EMBER.colors.textLight,
                  lineHeight: 21,
                }}
              >
                • {dataPoint.title}
              </Text>
            ))}
          </View>
        </View>
      ) : null}

      <View style={{ marginTop: 24 }}>
        <SectionTitle icon={DollarSign}>
          {t("learn.test_detail.section_price")}
        </SectionTitle>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
            marginTop: 10,
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 18,
              fontWeight: "700",
              color: EMBER.colors.text,
            }}
          >
            ${body.price}
          </Text>
          {body.originalPrice != null && (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                color: EMBER.colors.textFaint,
                textDecorationLine: "line-through",
              }}
            >
              ${body.originalPrice}
            </Text>
          )}
        </View>
      </View>

      {body.orderUrl ? (
        <BrownButton
          label={t("learn.test_detail.order_from_provider")}
          onPress={() => Linking.openURL(body.orderUrl!).catch(() => {})}
          leading={<ExternalLink size={16} color="#fff" strokeWidth={2} />}
          style={{ marginTop: 28 }}
        />
      ) : (
        <OrderKitButton productId={productId} />
      )}
    </>
  );
}

/* ─── Modal Wrapper ─── */

type Props = {
  item: ContentBlock<"test"> | ContentBlock<"product"> | null;
  onClose?: () => void;
  returnTab?: keyof MainTabParamList;
};

export default function TestDetailSheet({ item, onClose, returnTab }: Props) {
  if (!item) return null;

  return (
    <CanonicalTestRouteLauncher
      item={item}
      onClose={onClose}
      returnTab={returnTab}
    />
  );
}

function CanonicalTestRouteLauncher({
  item,
  onClose,
  returnTab,
}: {
  item: NonNullable<Props["item"]>;
  onClose?: () => void;
  returnTab?: keyof MainTabParamList;
}) {
  const navigation = useNavigation();
  const target = useMemo(() => getTestRouteTarget(String(item.id)), [item.id]);

  useEffect(() => {
    onClose?.();
    navigation.dispatch(
      CommonActions.navigate({
        name: "Access",
        params: {
          screen: target.screen,
          params: returnTab ? { ...target.params, returnTab } : target.params,
        },
      }),
    );
  }, [navigation, onClose, returnTab, target]);

  return null;
}
